/**
 * CareDocs design tokens.
 *
 * Built on top of Fluent UI 9 tokens. Components must consume tokens from
 * `tokens` (Fluent's runtime token map) or the semantic aliases below — never
 * hardcode color/spacing/font values.
 *
 * To re-skin the app, change the values in `caredocsBrand.ts` and the spacing
 * scale below. No component change should be required.
 */

export const spacing = {
  xxs: '2px',
  xs: '4px',
  s: '8px',
  m: '12px',
  l: '16px',
  xl: '20px',
  xxl: '24px',
  xxxl: '32px',
  xxxxl: '40px',
  xxxxxl: '56px',
} as const;

export type SpacingToken = keyof typeof spacing;

export const radius = {
  none: '0',
  s: '4px',
  m: '6px',
  l: '8px',
  xl: '12px',
  pill: '999px',
  circle: '50%',
} as const;

export type RadiusToken = keyof typeof radius;

export const elevation = {
  none: '0',
  low: '0 1px 2px rgba(0, 0, 0, 0.16)',
  medium: '0 2px 6px rgba(0, 0, 0, 0.2)',
  high: '0 8px 24px rgba(0, 0, 0, 0.28)',
} as const;

export type ElevationToken = keyof typeof elevation;

export const motion = {
  durationFast: '120ms',
  durationNormal: '200ms',
  durationSlow: '320ms',
  easingStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easingDecelerate: 'cubic-bezier(0, 0, 0.2, 1)',
  easingAccelerate: 'cubic-bezier(0.4, 0, 1, 1)',
} as const;

export const typography = {
  displayLarge: { fontSize: '32px', lineHeight: '40px', fontWeight: 600 },
  display: { fontSize: '28px', lineHeight: '36px', fontWeight: 600 },
  titleLarge: { fontSize: '22px', lineHeight: '28px', fontWeight: 600 },
  title: { fontSize: '18px', lineHeight: '24px', fontWeight: 600 },
  bodyLarge: { fontSize: '16px', lineHeight: '24px', fontWeight: 400 },
  body: { fontSize: '14px', lineHeight: '20px', fontWeight: 400 },
  bodyStrong: { fontSize: '14px', lineHeight: '20px', fontWeight: 600 },
  caption: { fontSize: '12px', lineHeight: '16px', fontWeight: 400 },
  numericLarge: {
    fontSize: '34px',
    lineHeight: '40px',
    fontWeight: 600,
    fontFeatureSettings: '"tnum" 1',
  },
  numeric: { fontSize: '20px', lineHeight: '24px', fontWeight: 600, fontFeatureSettings: '"tnum" 1' },
} as const;

export type TypographyToken = keyof typeof typography;

/**
 * Semantic status palette used by `StatusPill`, charts, and rollups.
 * Values reference Fluent's status tokens via CSS variables so they swap with the theme.
 */
export const statusPalette = {
  healthy: 'var(--colorPaletteGreenForeground1)',
  healthyBg: 'var(--colorPaletteGreenBackground2)',
  degraded: 'var(--colorPaletteYellowForeground1)',
  degradedBg: 'var(--colorPaletteYellowBackground2)',
  unhealthy: 'var(--colorPaletteRedForeground1)',
  unhealthyBg: 'var(--colorPaletteRedBackground2)',
  unknown: 'var(--colorNeutralForeground3)',
  unknownBg: 'var(--colorNeutralBackground3)',
} as const;

export type StatusPaletteToken = keyof typeof statusPalette;

/** Per-environment accent for the env switcher and badges. */
export const envAccent = {
  dev: { fg: 'var(--colorPaletteTealForeground2)', bg: 'var(--colorPaletteTealBackground2)' },
  staging: { fg: 'var(--colorPaletteMarigoldForeground1)', bg: 'var(--colorPaletteMarigoldBackground2)' },
  prod: { fg: 'var(--colorPaletteRedForeground1)', bg: 'var(--colorPaletteRedBackground2)' },
  corp: { fg: 'var(--colorPalettePurpleForeground2)', bg: 'var(--colorPalettePurpleBackground2)' },
} as const;
